import RestSet, { defaultRestSet } from '@common/interfaces/api/rest-set';
import Mission from '@common/interfaces/robot/missions/mission';
import CleaningZone from '@common/interfaces/robot/rooms/cleaning-zone';
import TeachAndRepeatZonePath from '@common/interfaces/robot/rooms/teach-and-repeat-zone-path';
import FloorData from '@common/interfaces/robot/rooms/floor-data';
import Gate from '@common/interfaces/robot/rooms/gate';
import Landmark from '@common/interfaces/robot/rooms/landmark';
import Point from '@common/interfaces/robot/rooms/point';
import RedZone from '@common/interfaces/robot/rooms/red-zone';
import RuleZone from '@common/interfaces/robot/rooms/rule-zone';
import RoomData from '@common/interfaces/robot/rooms/room-data';
import RoomLimitZone from '@common/interfaces/robot/rooms/room-limit-zone';
import {
  WaypointEdge,
  WaypointNode,
  WaypointSetValidationResult,
} from '@common/interfaces/robot/rooms/waypoints';
import ScheduleEntry, {
  ScheduleEntryState,
} from '@common/interfaces/robot/scheduling/schedule-entry';
import ZonePlannerState, {
  defaultZonePlannerState,
} from '@common/store/room/zone-planner/zone-planner-state';
import TimeSpan from '@common/utils/time-span';
import Weekday from '@common/utils/weekday';
import SchedulerControllerMouseState from '@common/enums/robot/rooms/scheduler-controller-mouse-state';
import Error from '@common/interfaces/misc/errors';
import PaginationWrapper from 'models/misc/api/pagination-wrapper';

export const LAYERS_LIST_ZONES_KEY = 'layers-list-zones-key';
export const LAYERS_LIST_GATES_KEY = 'layers-list-gates-key';
export const LAYERS_LIST_WAYPOINTS_KEY = 'layers-list-waypoints-key';
export const LAYERS_LIST_LANDMARKS_KEY = 'layers-list-landmarks-key';

interface LocalRobotState {
  floors: {
    data: FloorData[];
    rest: RestSet;
  };
  rooms: {
    data: RoomData[];
    selectedId: string | null;
    rest: RestSet;
  };
  missions: {
    data: Mission[];
    globalMissions: Mission[];
    selectedId: string | null;
    rest: RestSet;
  };
  schedules: {
    data: ScheduleEntryState[];
    rest: RestSet;
    schedulerMouseState: SchedulerControllerMouseState;
    selectedScheduleEntry: {
      selectedEntry: ScheduleEntry | null;
      initialTime: TimeSpan | null;
      initialDay: Weekday | null;
    };
  };
  cleaningZones: {
    data: CleaningZone[];
    rest: RestSet;
  };
  teachRepeatPaths: {
    data: TeachAndRepeatZonePath[];
    rest: RestSet;
  };
  redZones: {
    data: RedZone[];
    rest: RestSet;
  };
  limitZones: {
    data: RoomLimitZone[];
    rest: RestSet;
    selectedPointId: string | null;
    prevDragPoint: Point | null;
  };
  ruleZones: {
    data: RuleZone[];
    rest: RestSet;
  };
  landmarks: {
    data: Landmark[];
    rest: RestSet;
    selectedId: string | null;
  };
  waypoints: {
    data: {
      edges: WaypointEdge[];
      nodes: WaypointNode[];
    };
    rest: RestSet;
    selectedNodeId: string | null;
    selectedEdgeId: string | null;
    mousePosition: Point | null;
    isVisible: boolean;
    unsavedWaypoints: WaypointSetValidationResult[];
  };
  gates: {
    data: Gate[];
    rest: RestSet;
    selectedId: string | null;
    selectedPointId: string | null;
  };
  zonePlanner: ZonePlannerState;
  errors: {
    data: PaginationWrapper<Error> | null;
    rest: RestSet;
  };
}

export const getInitialLocalRobotState = (): LocalRobotState => {
  return {
    floors: {
      data: [],
      rest: defaultRestSet,
    },
    rooms: {
      data: [],
      selectedId: null,
      rest: defaultRestSet,
    },
    missions: {
      data: [],
      globalMissions: [],
      selectedId: null,
      rest: defaultRestSet,
    },
    schedules: {
      data: [],
      rest: defaultRestSet,
      schedulerMouseState: SchedulerControllerMouseState.Default,
      selectedScheduleEntry: {
        selectedEntry: null,
        initialTime: null,
        initialDay: null,
      },
    },
    cleaningZones: {
      data: [],
      rest: defaultRestSet,
    },
    teachRepeatPaths: {
      data: [],
      rest: defaultRestSet,
    },
    redZones: { data: [], rest: defaultRestSet },
    limitZones: {
      data: [],
      rest: defaultRestSet,
      prevDragPoint: null,
      selectedPointId: null,
    },
    ruleZones: {
      data: [],
      rest: defaultRestSet,
    },
    landmarks: {
      data: [],
      rest: defaultRestSet,
      selectedId: null,
    },
    waypoints: {
      data: { edges: [], nodes: [] },
      rest: defaultRestSet,
      selectedNodeId: null,
      selectedEdgeId: null,
      mousePosition: null,
      isVisible: localStorage.getItem(LAYERS_LIST_WAYPOINTS_KEY) === 'true',
      unsavedWaypoints: [],
    },
    gates: {
      data: [],
      rest: defaultRestSet,
      selectedId: null,
      selectedPointId: null,
    },
    zonePlanner: defaultZonePlannerState,
    errors: {
      data: null,
      rest: defaultRestSet,
    },
  };
};
export type { LocalRobotState };
